<template>
  <header
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-3' : 'bg-transparent py-5'
    ]"
  >
    <div class="container mx-auto px-4 flex items-center justify-between">
      <!-- Logo -->
      <router-link to="/" class="flex items-center space-x-2">
        <img v-if="settings.logo" :src="settings.logo" alt="Logo" class="w-10 h-10 object-contain" />
        <div v-else class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
          <span class="text-white font-bold text-xl">{{ settings.companyName ? settings.companyName.charAt(0) : 'K' }}</span>
        </div>
        <span :class="['text-2xl font-bold tracking-tight', isScrolled ? 'text-slate-900' : 'text-white']">
          {{ settings.companyName }}
        </span>
      </router-link>

      <!-- Desktop Nav -->
      <nav class="hidden md:flex items-center space-x-8">
        <router-link
          v-for="item in settings.navItems"
          :key="item.href"
          :to="item.href"
          :class="[
            'text-sm font-medium transition-colors hover:text-blue-600',
            $route.path === item.href
              ? 'text-blue-600'
              : isScrolled ? 'text-slate-600' : 'text-slate-100'
          ]"
        >
          {{ item.name }}
        </router-link>
        <div :class="[
          'flex items-center space-x-2 px-4 py-2 rounded-full border transition-all',
          isScrolled ? 'border-blue-600 text-blue-600' : 'border-white/30 text-white hover:bg-white/10'
        ]">
          <!-- Phone icon -->
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          <span class="text-sm font-bold">{{ settings.phone }}</span>
        </div>
      </nav>

      <!-- Mobile toggle -->
      <button
        :class="['md:hidden p-2 rounded-md', isScrolled ? 'text-slate-900' : 'text-white']"
        @click="isOpen = !isOpen"
      >
        <svg v-if="isOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>
    </div>

    <!-- Mobile Menu -->
    <transition name="slide-down">
      <div v-if="isOpen" class="fixed inset-0 top-[70px] bg-white z-40 md:hidden flex flex-col p-6 space-y-4">
        <router-link
          v-for="item in settings.navItems"
          :key="item.href"
          :to="item.href"
          :class="[
            'text-lg font-medium py-2 border-b border-slate-100',
            $route.path === item.href ? 'text-blue-600' : 'text-slate-600'
          ]"
          @click.native="isOpen = false"
        >
          {{ item.name }}
        </router-link>
        <div class="pt-4 flex items-center space-x-2 text-blue-600">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          <span class="text-lg font-bold">{{ settings.phone }}</span>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AppHeader',
  data() {
    return {
      isOpen: false,
      isScrolled: false
    }
  },
  computed: {
    ...mapGetters(['siteSettings']),
    settings() {
      return this.siteSettings
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    // 应用字体设置
    if (this.settings.fonts) {
      document.documentElement.style.fontFamily = this.settings.fonts.primary || 'Inter, system-ui, sans-serif'
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    }
  }
}
</script>

<style scoped>
.slide-down-enter-active, .slide-down-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}
.slide-down-enter, .slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
